import { daysUntilIso } from '../../../../../common/utils/format-utils.js'
import type { UpcomingEvent } from '../../utils/portal-api.js'

/**
 * The single inline badge shown on a directory row:
 *  - `joining`  — a future hire who isn't working yet (any horizon)
 *  - `leaving`  — a known last day today or ahead (any horizon)
 *  - `event`    — the person's soonest upcoming lifecycle event (within a week)
 */
export type RowBadge =
    | { kind: 'joining'; daysUntil: number }
    | { kind: 'leaving'; daysUntil: number }
    | { kind: 'event'; event: UpcomingEvent }

/** Upcoming events are only badged when they land within this many days. */
export const EVENT_WINDOW_DAYS = 7

/**
 * Soonest non-`first_day` feed event per employee. The feed arrives pre-sorted
 * by `daysUntil` ascending, so the first event seen for an employee is the
 * soonest. `first_day` is dropped because the joining badge already covers new
 * hires (and isn't capped to the event window).
 */
export const buildRowEventMap = (events: UpcomingEvent[]): Map<number, UpcomingEvent> => {
    const map = new Map<number, UpcomingEvent>()
    for (const event of events) {
        if (event.type === 'first_day') continue
        if (!map.has(event.employeeId)) map.set(event.employeeId, event)
    }
    return map
}

/**
 * Pick the row's badge. A future hire date outranks everything (they're not
 * here yet, so a birthday-this-week is noise); a known last day outranks plain
 * events for the same reason. Returns null when nothing applies.
 * `hireDate === today` (or past) yields no joining badge — they're already
 * working. `employmentEndDate === today` still badges — it's their last day —
 * but a past end date doesn't (the offboarding sweep retires them anyway).
 */
export const selectRowBadge = (
    hireDate: string | null,
    employmentEndDate: string | null,
    event: UpcomingEvent | undefined,
    today: Date,
): RowBadge | null => {
    if (hireDate) {
        const daysUntil = daysUntilIso(hireDate, today)
        if (daysUntil !== null && daysUntil > 0) return { kind: 'joining', daysUntil }
    }
    if (employmentEndDate) {
        const daysUntil = daysUntilIso(employmentEndDate, today)
        if (daysUntil !== null && daysUntil >= 0) return { kind: 'leaving', daysUntil }
    }
    if (event && event.daysUntil <= EVENT_WINDOW_DAYS) return { kind: 'event', event }
    return null
}
